import React, { useEffect, useState } from 'react';

interface SplashScreenProps {
  onComplete?: () => void;
  isLoadingMedias?: boolean;
  hasMedias?: boolean;
  isActivated?: boolean;
  hasActiveSubscription?: boolean;
}

const SplashScreen: React.FC<SplashScreenProps> = ({
  onComplete,
  isLoadingMedias = false,
  hasMedias = false,
  isActivated = false,
  hasActiveSubscription = false
}) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'init' | 'loading_medias' | 'complete'>('init');

  useEffect(() => {
    console.log('[SPLASH] SplashScreen iniciado com props:', {
      isLoadingMedias,
      hasMedias,
      isActivated,
      hasActiveSubscription,
      phase,
      progress
    });

    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;

    // Timeout de segurança - máximo 10 segundos no splash
    timeoutId = setTimeout(() => {
      console.log('🕐 SplashScreen timeout - forçando conclusão após 10s');
      setPhase('complete');
      setProgress(100);
    }, 10000);

    // Fase inicial de carregamento
    if (phase === 'init') {
      console.log('[SPLASH] Iniciando fase init');
      intervalId = setInterval(() => {
        setProgress(prev => {
          if (prev >= 50) {
            clearInterval(intervalId);
            console.log('[SPLASH] Mudando para fase loading_medias');
            setPhase('loading_medias');
            return 50;
          }
          return prev + 5;
        });
      }, 100);
    }

    // Fase de carregamento de mídias
    if (phase === 'loading_medias') {
      console.log('[SPLASH] Fase loading_medias - hasMedias:', hasMedias, 'isActivated:', isActivated);

      // Se tem mídias, completa normalmente
      if (hasMedias && isActivated) {
        console.log('[SPLASH] Tem mídias e está ativado - completando');
        intervalId = setInterval(() => {
          setProgress(prev => {
            if (prev >= 100) {
              clearInterval(intervalId);
              setPhase('complete');
              return 100;
            }
            return prev + 10;
          });
        }, 100);
      }
      // Se está ativado mas sem mídias, completa rapidamente
      // Isso evita loop infinito quando dispositivo foi ativado via Companion
      // mas não tem tela/mídias associadas
      else if (isActivated && hasActiveSubscription) {
        console.log('[SPLASH] Ativado sem mídias - completando rapidamente para evitar loop');
        // Iniciar progressão imediata para sair do splash
        intervalId = setInterval(() => {
          setProgress(prev => {
            if (prev >= 100) {
              clearInterval(intervalId);
              setPhase('complete');
              return 100;
            }
            return prev + 20; // Progressão mais rápida
          });
        }, 100);
      }
      // Se não está ativado, força conclusão mais rápido
      else {
        console.log('[SPLASH] Não ativado - forçando conclusão');
        setTimeout(() => {
          setPhase('complete');
          setProgress(100);
        }, 3000);
      }
    }

    // Fase de conclusão
    if (phase === 'complete') {
      clearTimeout(timeoutId); // Cancelar timeout quando completo
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [phase, hasMedias, isActivated, hasActiveSubscription, onComplete]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A3622] to-[#071E14] flex flex-col items-center justify-center p-8">
      <div className="text-center space-y-8 max-w-lg">
        {/* Logo */}
        <div className="w-48 h-48 mx-auto relative">
          <div className="absolute inset-0 bg-[#39b92c] opacity-20 blur-xl rounded-full"></div>
          <img
            src="https://locaninja.com/logotipo-watch-white.png"
            alt="Watch Logo"
            className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
          />
        </div>

        {/* Texto */}
        <h1 className="text-4xl font-bold text-white">Iniciando Player</h1>

        {/* Barra de progresso */}
        <div className="w-full bg-[#071E14] rounded-full h-4 border border-[#39b92c]/30">
          <div
            className="bg-[#39b92c] h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <p className="text-[#A3E0C6]">
          {phase === 'init' && 'Iniciando player...'}
          {phase === 'loading_medias' && (hasMedias ? 'Carregando mídias...' : 'Aguardando mídias...')}
          {phase === 'complete' && 'Concluído!'}
        </p>
      </div>
    </div>
  );
};

export default SplashScreen;
